import { useCallback, useEffect, useState } from 'react';
import { listAllEmployees, peopleGateway } from '../../people';
import { payConfigGateway } from '../../pay-config';
import { healthcareRatesGateway } from '../../healthcare-rates/index';
import { customPayRunGateway } from '../index';
import type { EarningLineResponse, PayrollPreviewResponse, PreviewEmployeeLineResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { formatQuantity } from '../../healthcare-rates/money';
import { payBasisLabels, quantityUnits } from '../../healthcare-rates/types';

export interface PayrollRecord {
  employeeId: string;
  name: string;
  /** Job title from Employees; '—' when it cannot be read. */
  role: string;
  /** Healthcare: the discipline snapshot of each service line on the run (as stored when the line was saved), without repeats; empty when the run has none. */
  disciplines: string[];
  /** Names of the earning codes on the run's custom earning lines; empty when the run adds none. */
  payType: string;
  /** Human text for the run's custom earning lines ("$20.00 x 8 hrs", "$150.00 flat"). */
  earnings: string;
  line: PreviewEmployeeLineResponse;
  /** False when the calculation raised an exception for this employee — they cannot be paid in this run. */
  canProcess: boolean;
  /** EPN-240: this employee would be paid by this run but has no usable pay method. Must be fixed or removed; never acknowledgeable. */
  payMethodBlocked: boolean;
}

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** A saved service line carries ids only (the server does not echo names), so the service type name comes from the catalog when the line has no snapshot of its own. */
export function describeEarningLine(l: Pick<EarningLineResponse, 'hours' | 'rate' | 'flatAmount' | 'serviceLine'>, serviceTypeNames?: ReadonlyMap<string, string>): string {
  if (l.serviceLine) {
    const s = l.serviceLine;
    const unit = s.quantity === 1 ? quantityUnits[s.payBasis].singular : quantityUnits[s.payBasis].plural;
    const service = s.serviceTypeName ?? (s.serviceTypeId ? serviceTypeNames?.get(s.serviceTypeId) : undefined);
    return `${formatQuantity(s.quantity)} ${unit}, ${payBasisLabels[s.payBasis]}${service ? ` (${service})` : ''}`;
  }
  if (l.flatAmount != null) return `${money(l.flatAmount)} flat`;
  if (l.hours != null && l.rate != null) return `${money(l.rate)} x ${l.hours} hrs`;
  return '—';
}

export function earningLineAmount(l: Pick<EarningLineResponse, 'hours' | 'rate' | 'flatAmount'>): number {
  return l.flatAmount ?? (l.hours != null && l.rate != null ? l.hours * l.rate : 0);
}

export interface PayrollRecordsState {
  loading: boolean;
  error: string | null;
  preview: PayrollPreviewResponse | null;
  records: PayrollRecord[];
  reload: () => Promise<void>;
}

/** The per-employee records behind Review, Pending and Approved Payroll: the existing calculate result + exceptions
 * (`getPreview`), joined with the run's earning lines, earning-code names and each employee's job title. Everything is
 * read from existing gateways; nothing here is a source of truth. */
export function usePayrollRecords(runId: string, enabled: boolean): PayrollRecordsState {
  const [state, setState] = useState<Omit<PayrollRecordsState, 'reload'>>({ loading: enabled, error: null, preview: null, records: [] });

  const load = useCallback(async () => {
    if (!enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const preview = await customPayRunGateway.getPreview(runId);
      // Context lookups are best-effort: a missing read permission blanks a column, it does not block the table.
      const [lines, codes, employees, jobTitles, disciplines, serviceTypes] = await Promise.all([
        customPayRunGateway.getEarnings(runId).catch(() => [] as EarningLineResponse[]),
        payConfigGateway.listEarningCodes().catch(() => []),
        listAllEmployees(peopleGateway).catch(() => []),
        peopleGateway.listJobTitles().catch(() => []),
        healthcareRatesGateway.listDisciplines({ includeInactive: true }).catch(() => []),
        healthcareRatesGateway.listServiceTypes({ includeInactive: true }).catch(() => []),
      ]);
      const serviceTypeNames = new Map(serviceTypes.map((t) => [t.id, t.name]));
      const disciplineNames = new Map(disciplines.map((d) => [d.id, d.name]));
      const codeNames = new Map(codes.map((c) => [c.id, c.name]));
      const titleNames = new Map(jobTitles.map((t) => [t.id, t.name]));
      const employeesById = new Map(employees.map((e) => [e.id, e]));

      const records: PayrollRecord[] = preview.employees.map((line) => {
        const own = lines.filter((l) => l.employeeId === line.employeeId);
        const titleId = employeesById.get(line.employeeId)?.jobTitleId;
        return {
          employeeId: line.employeeId,
          name: `${line.firstName} ${line.lastName}`.trim(),
          role: (titleId && titleNames.get(titleId)) || '—',
          disciplines: [
            ...new Set([
              ...(line.earningDetails ?? []).map((d) => d.disciplineName),
              ...own.map((l) => (l.serviceLine ? (l.serviceLine.disciplineName ?? disciplineNames.get(l.serviceLine.disciplineId)) : null)),
            ].filter((n): n is string => Boolean(n))),
          ],
          payType: [...new Set(own.map((l) => codeNames.get(l.earningCodeId) ?? l.label))].join(', '),
          earnings: own.map((l) => describeEarningLine(l, serviceTypeNames)).join('; '),
          line,
          canProcess: !line.exceptionFlags,
          payMethodBlocked: line.payMethodBlocked === true,
        };
      });
      setState({ loading: false, error: null, preview, records });
    } catch (err) {
      setState({ loading: false, error: friendlyErrorMessage(err, 'Could not load the calculated payroll for this run.'), preview: null, records: [] });
    }
  }, [runId, enabled]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  return { ...state, reload: load };
}
